import { useState, useEffect, useCallback } from 'react';
import type { MusicProvider, MusicProviderType, Track } from '@/types/music-provider';
import { SpotifyProvider } from '@/providers/music/SpotifyProvider';
import { AppleMusicProvider } from '@/providers/music/AppleMusicProvider';
import { DeezerProvider } from '@/providers/music/DeezerProvider';
import { useToast } from '@/hooks/use-toast';

export const useMusicProvider = () => {
  const [currentProvider, setCurrentProvider] = useState<MusicProvider | null>(null);
  const [selectedProviderType, setSelectedProviderType] = useState<MusicProviderType>('spotify');
  const [isInitializing, setIsInitializing] = useState(false);
  const { toast } = useToast();

  // Inicializar provider
  const initializeProvider = useCallback(async (type: MusicProviderType) => {
    setIsInitializing(true);
    try {
      let provider: MusicProvider;
      
      switch (type) {
        case 'spotify':
          provider = new SpotifyProvider();
          break;
        case 'apple':
          provider = new AppleMusicProvider();
          break;
        case 'deezer':
          provider = new DeezerProvider();
          break;
        default:
          throw new Error('Provider não suportado');
      }

      await provider.checkAuthStatus();
      setCurrentProvider(provider);
      setSelectedProviderType(type);
      
      // Salvar preferência
      localStorage.setItem('preferred_music_provider', type);
    } catch (error) {
      console.error('Erro ao inicializar provider:', error);
      toast({
        title: 'Erro ao conectar',
        description: 'Não foi possível inicializar o serviço de música',
        variant: 'destructive'
      });
    } finally {
      setIsInitializing(false);
    }
  }, [toast]);

  // Carregar provider preferido ao montar
  useEffect(() => {
    const preferred = localStorage.getItem('preferred_music_provider') as MusicProviderType;
    initializeProvider(preferred || 'spotify');
  }, [initializeProvider]);

  // Conectar ao provider
  const connect = useCallback(async () => {
    if (!currentProvider) return;
    
    try {
      await currentProvider.login();
      toast({
        title: 'Conectado',
        description: `${currentProvider.name} conectado com sucesso!`
      });
    } catch (error) {
      console.error('Erro ao conectar:', error);
      toast({
        title: 'Erro na conexão',
        description: `Não foi possível conectar ao ${currentProvider.name}`,
        variant: 'destructive'
      });
    }
  }, [currentProvider, toast]);

  // Desconectar do provider
  const disconnect = useCallback(async () => {
    if (!currentProvider) return;
    
    try {
      await currentProvider.logout();
      toast({
        title: 'Desconectado',
        description: `${currentProvider.name} desconectado`
      });
    } catch (error) {
      console.error('Erro ao desconectar:', error);
    }
  }, [currentProvider, toast]);

  // Trocar provider
  const switchProvider = useCallback(async (type: MusicProviderType) => {
    if (currentProvider) {
      await disconnect();
    }
    await initializeProvider(type);
  }, [currentProvider, disconnect, initializeProvider]);

  // Controles de playback
  const play = useCallback(async (trackId?: string) => {
    if (!currentProvider?.isAuthenticated) {
      toast({
        title: 'Não conectado',
        description: 'Conecte-se a um serviço de música primeiro',
        variant: 'destructive'
      });
      return;
    }
    await currentProvider.play(trackId);
  }, [currentProvider, toast]);

  const pause = useCallback(async () => {
    if (currentProvider) await currentProvider.pause();
  }, [currentProvider]);

  const resume = useCallback(async () => {
    if (currentProvider) await currentProvider.resume();
  }, [currentProvider]);

  const next = useCallback(async () => {
    if (currentProvider) await currentProvider.next();
  }, [currentProvider]);

  const previous = useCallback(async () => {
    if (currentProvider) await currentProvider.previous();
  }, [currentProvider]);

  const seek = useCallback(async (positionMs: number) => {
    if (currentProvider) await currentProvider.seek(positionMs);
  }, [currentProvider]);

  const setVolume = useCallback(async (volume: number) => {
    if (currentProvider) await currentProvider.setVolume(volume);
  }, [currentProvider]);

  // Busca e recomendações
  const searchTracks = useCallback(async (query: string, limit?: number): Promise<Track[]> => {
    if (!currentProvider?.isAuthenticated) return [];
    return currentProvider.searchTracks(query, limit);
  }, [currentProvider]);

  const getRecommendations = useCallback(async (seedTracks: string[], targetBPM?: number): Promise<Track[]> => {
    if (!currentProvider?.isAuthenticated) return [];
    return currentProvider.getRecommendations(seedTracks, targetBPM);
  }, [currentProvider]);

  return {
    // Estado
    currentProvider,
    selectedProviderType,
    isInitializing,
    isAuthenticated: currentProvider?.isAuthenticated || false,
    isReady: currentProvider?.isReady || false,
    playerState: currentProvider?.getPlayerState() || null,
    currentTrack: currentProvider?.getCurrentTrack() || null,

    // Gerenciamento de provider
    switchProvider,
    connect,
    disconnect,

    // Controles de playback
    play,
    pause,
    resume,
    next,
    previous,
    seek,
    setVolume,

    // Busca e recomendações
    searchTracks,
    getRecommendations
  };
};
